/**
 * Gamification API (client-safe)
 *
 * No .server suffix so client hooks can import without triggering
 * TanStack Start import-protection. Handlers run only on the server.
 *
 * Server-only: user stats, achievements, streaks, leaderboard.
 * Uses Service Role Key with table whitelisting and JWT auth.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

const LEADERBOARD_PERIOD = z.enum(["day", "week", "month", "all_time"]);
const GAMIFICATION_TABLES = [
  "profiles",
  "todos",
  "point_transactions",
  "user_achievements",
  "user_earned_badges",
  "user_current_ranks",
] as const;
const ALLOWED_TABLES = z.enum(GAMIFICATION_TABLES);

export interface GamificationStatsPayload {
  totalXP: number;
  xpToday: number;
  xpThisWeek: number;
  xpThisMonth: number;
  currentStreak: number;
  longestStreak: number;
  tasksCompleted: number;
  lastTaskCompletedAt: string | undefined;
}

export interface LeaderboardEntryPayload {
  user_id: string;
  username: string;
  full_name: string;
  display_name?: string;
  avatar_url: string;
  xp: number;
  xp_today?: number;
  xp_this_week: number;
  xp_this_month: number;
  current_streak: number;
  longest_streak: number;
  tasks_completed: number;
  rank: number;
}

export interface UserAchievementPayload {
  id: string;
  user_id: string;
  achievement_type: string;
  achievement_id: string;
  name: string;
  description?: string | null;
  emoji?: string | null;
  rarity?: string | null;
  earned_at?: string | null;
}

/**
 * Get gamification stats for a user (XP, streaks, tasks completed).
 * Tables: profiles, todos (whitelisted).
 */
export const getUserGamificationStats = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const table = "profiles";
    if (!ALLOWED_TABLES.safeParse(table).success) {
      return { data: null, error: "Invalid table access" };
    }

    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: profile, error: profileError } = await client
      .from("profiles")
      .select(
        "xp, xp_today, xp_this_week, xp_this_month, current_streak, longest_streak, last_task_completed_at"
      )
      .eq("id", data.userId)
      .single();

    if (profileError) return { data: null, error: profileError.message };
    if (!profile) return { data: null, error: "Profile not found" };

    const { count, error: countError } = await client
      .from("todos")
      .select("id", { count: "exact", head: true })
      .eq("created_by", data.userId)
      .eq("status", "completed");

    if (countError) return { data: null, error: countError.message };

    const payload: GamificationStatsPayload = {
      totalXP: Number(profile.xp) || 0,
      xpToday: Number(profile.xp_today) || 0,
      xpThisWeek: Number(profile.xp_this_week) || 0,
      xpThisMonth: Number(profile.xp_this_month) || 0,
      currentStreak: Number(profile.current_streak) || 0,
      longestStreak: Number(profile.longest_streak) || 0,
      tasksCompleted: count ?? 0,
      lastTaskCompletedAt: profile.last_task_completed_at ?? undefined,
    };

    return { data: payload, error: null };
  });

/**
 * Get achievements/badges for a user.
 * Tables: user_achievements, user_earned_badges (whitelisted).
 */
export const getAchievements = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    try {
      const { data: achievements, error } = await client
        .from("user_achievements")
        .select("id, user_id, achievement_type, achievement_id, name, description, emoji, rarity, earned_at")
        .eq("user_id", data.userId)
        .order("earned_at", { ascending: false });

      if (error) return { data: [], error: error.message };

      const payload: UserAchievementPayload[] = (achievements ?? []).map((row: Record<string, unknown>) => ({
        id: String(row.id),
        user_id: String(row.user_id),
        achievement_type: String(row.achievement_type ?? ""),
        achievement_id: String(row.achievement_id ?? ""),
        name: String(row.name ?? ""),
        description: row.description != null ? String(row.description) : null,
        emoji: row.emoji != null ? String(row.emoji) : null,
        rarity: row.rarity != null ? String(row.rarity) : null,
        earned_at: row.earned_at != null ? String(row.earned_at) : null,
      }));

      return { data: payload, error: null };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return { data: [], error: message };
    }
  });

/**
 * Get leaderboard for a period.
 * all_time: profiles ordered by xp; day/week/month could use point_transactions aggregation — we use profiles for consistency.
 */
export const getLeaderboard = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      period: LEADERBOARD_PERIOD.default("all_time"),
      limit: z.number().int().min(1).max(100).default(20),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const selectCols =
      "id, username, full_name, avatar_url, xp, xp_today, xp_this_week, xp_this_month, current_streak, longest_streak";

    const { data: profiles, error } = await client
      .from("profiles")
      .select(selectCols)
      .order("xp", { ascending: false })
      .limit(Math.min(data.limit * 2, 200));

    if (error) return { data: null, error: error.message };

    const list = (profiles ?? []) as Array<{
      id: string;
      username: string | null;
      full_name: string | null;
      avatar_url: string | null;
      xp: number;
      xp_today: number;
      xp_this_week: number;
      xp_this_month: number;
      current_streak: number;
      longest_streak: number;
    }>;

    const userIds = list.map((p) => p.id);
    const tasksByUser: Record<string, number> = {};

    if (userIds.length > 0) {
      const { data: todos } = await client
        .from("todos")
        .select("created_by")
        .eq("status", "completed");
      const todoList = (todos ?? []) as Array<{ created_by: string }>;
      todoList.forEach((t) => {
        tasksByUser[t.created_by] = (tasksByUser[t.created_by] ?? 0) + 1;
      });
    }

    const entries: LeaderboardEntryPayload[] = list.slice(0, data.limit).map((p, i) => ({
      user_id: p.id,
      username: p.username ?? "",
      full_name: p.full_name ?? p.username ?? "",
      display_name: p.full_name ?? p.username ?? undefined,
      avatar_url: p.avatar_url ?? "",
      xp: Number(p.xp) || 0,
      xp_today: Number(p.xp_today) || 0,
      xp_this_week: Number(p.xp_this_week) || 0,
      xp_this_month: Number(p.xp_this_month) || 0,
      current_streak: Number(p.current_streak) || 0,
      longest_streak: Number(p.longest_streak) || 0,
      tasks_completed: tasksByUser[p.id] ?? 0,
      rank: i + 1,
    }));

    return { data: entries, error: null };
  });
